import { Component, inject, signal, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { Barcode, BarcodeType } from '../../models/sku.interface';

@Component({
  selector: 'app-barcode-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AppButtonComponent,
    DialogModule,
    InputTextModule,
    SelectModule,
  ],
  templateUrl: './barcode-dialog.component.html',
  styleUrl: './barcode-dialog.component.css',
})
export class BarcodeDialogComponent implements OnChanges {
  private readonly translateService = inject(TranslateService);

  private _visible = false;

  @Input()
  set visible(value: boolean) {
    this._visible = value;
    if (value) {
      setTimeout(() => {
        this.initializeForm();
      }, 0);
    }
  }

  get visible(): boolean {
    return this._visible;
  }

  @Input() initialData: Barcode | null = null;
  @Input() viewMode = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<Barcode>();

  showErrors = signal(false);

  formData = {
    barcodeValue: '',
    barcodeType: 'EAN' as BarcodeType,
  };

  get barcodeTypeOptions() {
    return [
      { label: 'EAN', value: 'EAN' },
      { label: 'GTIN', value: 'GTIN' },
      { label: 'UPC', value: 'UPC' },
      { label: 'GS1', value: 'GS1' },
      { label: 'CODE_39', value: 'CODE_39' },
      { label: 'ISBN', value: 'ISBN' },
      { label: 'ISSN', value: 'ISSN' },
      { label: 'JAN', value: 'JAN' },
      { label: 'PZN', value: 'PZN' },
      { label: 'OTHER', value: 'OTHER' },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData'] && this.visible) {
      setTimeout(() => {
        this.initializeForm();
      }, 0);
    }
  }

  private initializeForm(): void {
    if (this.initialData) {
      this.formData = {
        barcodeValue: this.initialData.barcodeValue || '',
        barcodeType: this.initialData.barcodeType || 'EAN',
      };
    } else {
      this.formData = {
        barcodeValue: '',
        barcodeType: 'EAN',
      };
    }
    this.showErrors.set(false);
  }

  onCancel(): void {
    this.visibleChange.emit(false);
  }

  onConfirm(): void {
    // Validate required fields
    if (!this.formData.barcodeValue || !this.formData.barcodeType) {
      this.showErrors.set(true);
      return;
    }

    const barcodeData: Barcode = {
      id: this.initialData?.id,
      barcodeValue: this.formData.barcodeValue.trim(),
      barcodeType: this.formData.barcodeType,
    };

    this.confirm.emit(barcodeData);
    this.visibleChange.emit(false);
  }
}
